import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { searchEntries } from "../matcher";
import type { SearchResult } from "../types";
import { useSearchEntries } from "../use-search-entries";

interface SearchPaletteDesktopProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Desktop Cmd+K palette. Reuses the same data layer (`useSearchEntries` +
 * `searchEntries`) as the mobile drawer; only the shell differs.
 *
 * `shouldFilter={false}` lets us bypass cmdk's built-in fuzzy matcher and
 * delegate ranking to our own scorer (which knows about diacritics and the
 * section/field boost).
 */
export function SearchPaletteDesktop({ open, onOpenChange }: SearchPaletteDesktopProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const [query, setQuery] = useState("");
  const entries = useSearchEntries();

  const results = useMemo(() => searchEntries(query, entries), [query, entries]);
  const sections = results.filter((result) => result.kind === "section");
  const fields = results.filter((result) => result.kind === "field");

  const handleSelect = (result: SearchResult) => {
    const path = `/settings/${result.sectionId}`;
    navigate(result.anchor ? `${path}#${result.anchor}` : path);
    setQuery("");
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setQuery("");
        onOpenChange(next);
      }}
    >
      <DialogContent className="overflow-hidden p-0 sm:max-w-4xl">
        <Command shouldFilter={false} className="rounded-lg">
          <CommandInput
            placeholder={t("search.placeholder")}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[70vh]">
            {query.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {t("search.emptyHint")}
              </div>
            ) : results.length === 0 ? (
              <CommandEmpty>{t("search.noResults")}</CommandEmpty>
            ) : (
              <>
                {sections.length > 0 && (
                  <CommandGroup heading={t("search.groupSections")}>
                    {sections.map((result, index) => (
                      <ResultItem
                        key={`section-${result.sectionId}-${index}`}
                        result={result}
                        onSelect={handleSelect}
                      />
                    ))}
                  </CommandGroup>
                )}
                {fields.length > 0 && (
                  <CommandGroup heading={t("search.groupFields")}>
                    {fields.map((result, index) => (
                      <ResultItem
                        key={`field-${result.sectionId}-${result.fieldId ?? index}`}
                        result={result}
                        onSelect={handleSelect}
                      />
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

interface ResultItemProps {
  result: SearchResult;
  onSelect: (result: SearchResult) => void;
}

function ResultItem({ result, onSelect }: ResultItemProps) {
  // cmdk requires a unique `value` to track selection; include label so
  // keyboard arrow navigation feels intuitive even with shouldFilter disabled.
  const value = `${result.kind}-${result.sectionId}-${result.fieldId ?? ""}-${result.label}`;
  return (
    <CommandItem value={value} onSelect={() => onSelect(result)}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{result.label}</div>
        {result.breadcrumb ? (
          <div className="text-xs text-muted-foreground truncate">{result.breadcrumb}</div>
        ) : null}
      </div>
    </CommandItem>
  );
}
